import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { adminGet, adminPatch, adminPut, adminPost } from './adminApi';
import { TenantIpPolicyCard } from './qr/TenantIpPolicyCard';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { ArrowLeft, Users } from 'lucide-react';
import BfsiSetupTab from './bfsi/BfsiSetupTab';
import BfsiRulesTab from './bfsi/BfsiRulesTab';
import BfsiProvisioningTab from './bfsi/BfsiProvisioningTab';
import BfsiComplianceTab from './bfsi/BfsiComplianceTab';
import BfsiOpsHealthTab from './bfsi/BfsiOpsHealthTab';

const mono = { fontFamily: "'IBM Plex Mono', monospace" };
const GREEN = '#0B6B55';

const FEATURE_LABELS = {
  temperature: 'Temperature monitoring', humidity: 'Humidity monitoring', gps: 'GPS tracking',
  bag_events: 'Bag open/close events', passport: 'Digital passports', geofence_lock: 'Geofence lock',
  weight_delta: 'Weight delta sensing',
};
const ALL_FEATURES = Object.keys(FEATURE_LABELS);

const TABS = ['Overview', 'Features', 'Security', 'Devices', 'Audit trail'];
const BFSI_TABS = ['BFSI setup', 'Rules & branches', 'Provisioning', 'Compliance', 'Ops health'];

const Card = ({ children, className = '', ...rest }) => (
  <div className={`rounded-lg border bg-white p-6 ${className}`} style={{ borderColor: '#E5E3DC' }} {...rest}>{children}</div>
);

function OverviewTab({ customer, onSaved }) {
  const [form, setForm] = useState({
    legal_name: customer.legal_name || '', contact_person: customer.contact_person || '',
    mobile: customer.mobile || '', email: customer.email || '', gstin: customer.gstin || '',
    drug_licence: customer.drug_licence || '', billing_address: customer.billing_address || '',
    seat_limit: customer.seat_limit || 25,
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await adminPatch(`/customers/${customer.id}`, { ...form, seat_limit: Number(form.seat_limit) });
      toast.success('Customer updated');
      onSaved();
    } catch (e) { toast.error(e.message); }
    setSaving(false);
  };

  const toggleStatus = async () => {
    const next = customer.status === 'SUSPENDED' ? 'ACTIVE' : 'SUSPENDED';
    try {
      await adminPatch(`/customers/${customer.id}`, { status: next });
      toast.success(next === 'SUSPENDED' ? 'Customer suspended' : 'Customer re-activated');
      onSaved();
    } catch (e) { toast.error(e.message); }
  };

  const f = (label, key, props = {}) => (
    <div>
      <label className="text-sm block mb-1" style={{ color: '#4b504a' }}>{label}</label>
      <Input data-testid={`cust-${key}-input`} value={form[key]} onChange={e => setForm({ ...form, [key]: e.target.value })}
        className="bg-white border-[#D9D6CD]" {...props} />
    </div>
  );

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="customer-overview-tab">
      <Card className="lg:col-span-2">
        <h3 className="font-bold mb-4" style={{ color: '#1B1D1C' }}>Company details</h3>
        <div className="grid grid-cols-2 gap-4">
          {f('Legal name', 'legal_name')}
          {f('Contact person', 'contact_person')}
          {f('Mobile', 'mobile')}
          {f('Email', 'email')}
          {f('GSTIN', 'gstin')}
          {f('Drug licence', 'drug_licence')}
          {f('Seat limit', 'seat_limit', { type: 'number' })}
        </div>
        <div className="mt-4">
          <label className="text-sm block mb-1" style={{ color: '#4b504a' }}>Billing address</label>
          <textarea data-testid="cust-billing-address-input" value={form.billing_address}
            onChange={e => setForm({ ...form, billing_address: e.target.value })} rows={2}
            className="w-full rounded-md border px-3 py-2 text-sm" style={{ borderColor: '#D9D6CD' }} />
        </div>
        <Button disabled={saving} data-testid="save-overview-btn" onClick={save}
          className="mt-4 text-white hover:opacity-90" style={{ background: GREEN }}>Save changes</Button>
      </Card>
      <div className="space-y-6">
        <Card>
          <h3 className="font-bold mb-3" style={{ color: '#1B1D1C' }}>Profile</h3>
          <dl className="text-sm space-y-2">
            {[['Code', customer.code], ['Vertical', customer.vertical_code || '—'],
              ['Type', customer.customer_type_code || '—'], ['Compliance', customer.compliance_tier || '—']].map(([k, v]) => (
              <div key={k} className="flex justify-between"><dt style={{ color: '#6b7069' }}>{k}</dt><dd style={mono}>{v}</dd></div>
            ))}
          </dl>
        </Card>
        <Card>
          <h3 className="font-bold mb-2" style={{ color: '#1B1D1C' }}>Account status</h3>
          <span className="px-2 py-0.5 rounded text-xs" style={customer.status === 'SUSPENDED'
            ? { background: '#FBEBEB', color: '#8C2B2B' } : { background: '#E7F2EE', color: GREEN }}>{customer.status}</span>
          <Button variant="outline" data-testid="toggle-status-btn" onClick={toggleStatus}
            className="w-full mt-3 bg-white border-[#D9D6CD]"
            style={customer.status !== 'SUSPENDED' ? { color: '#8C2B2B' } : { color: GREEN }}>
            {customer.status === 'SUSPENDED' ? 'Re-activate customer' : 'Suspend customer'}
          </Button>
        </Card>
      </div>
    </div>
  );
}

function FeaturesTab({ tenantId }) {
  const [features, setFeatures] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    adminGet(`/customers/${tenantId}/features`).then(list => {
      const map = Object.fromEntries(list.map(x => [x.feature, x]));
      setFeatures(ALL_FEATURES.map(code => map[code] || { feature: code, enabled: false, settings: {} }));
    }).catch(e => toast.error(e.message));
  }, [tenantId]);

  const save = async () => {
    setSaving(true);
    try {
      await adminPut(`/customers/${tenantId}/features`, features.filter(x => x.enabled));
      toast.success('Features saved');
    } catch (e) { toast.error(e.message); }
    setSaving(false);
  };

  if (!features) return <p className="text-sm" style={{ color: '#9a9e96' }}>Loading…</p>;
  return (
    <Card className="max-w-2xl" data-testid="customer-features-tab">
      <h3 className="font-bold mb-4" style={{ color: '#1B1D1C' }}>Enabled features</h3>
      <div className="divide-y" style={{ borderColor: '#F3F1EA' }}>
        {features.map((x, i) => (
          <div key={x.feature} className="flex items-center justify-between py-3">
            <div>
              <p className="text-sm font-medium" style={{ color: '#1B1D1C' }}>{FEATURE_LABELS[x.feature] || x.feature}</p>
              <p className="text-xs" style={{ ...mono, color: '#9a9e96' }}>{x.feature}</p>
            </div>
            <Switch data-testid={`feature-toggle-${x.feature}`} checked={x.enabled}
              onCheckedChange={v => setFeatures(features.map((y, j) => j === i ? { ...y, enabled: v } : y))} />
          </div>
        ))}
      </div>
      <Button disabled={saving} data-testid="save-features-btn" onClick={save}
        className="mt-4 text-white hover:opacity-90" style={{ background: GREEN }}>Save features</Button>
    </Card>
  );
}

function SecurityTab({ tenantId }) {
  const [sec, setSec] = useState(null);
  const [ipText, setIpText] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    adminGet(`/customers/${tenantId}/security`).then(s => {
      setSec(s); setIpText((s.ip_allowlist || []).join('\n'));
    }).catch(e => toast.error(e.message));
  }, [tenantId]);

  const save = async () => {
    setSaving(true);
    try {
      await adminPut(`/customers/${tenantId}/security`, {
        mfa_required: sec.mfa_required,
        session_timeout_minutes: Number(sec.session_timeout_minutes) || 480,
        ip_allowlist: ipText.split('\n').map(x => x.trim()).filter(Boolean),
        sso_provider: sec.sso_provider || null,
        scim_enabled: sec.scim_enabled,
      });
      toast.success('Security settings saved');
    } catch (e) { toast.error(e.message); }
    setSaving(false);
  };

  if (!sec) return <p className="text-sm" style={{ color: '#9a9e96' }}>Loading…</p>;
  return (
    <Card className="max-w-2xl" data-testid="customer-security-tab">
      <h3 className="font-bold mb-4" style={{ color: '#1B1D1C' }}>Security policy</h3>
      <div className="space-y-5">
        <div className="flex items-center justify-between">
          <div><p className="text-sm font-medium">Require MFA</p>
            <p className="text-xs" style={{ color: '#9a9e96' }}>All customer users must set up two-factor auth</p></div>
          <Switch data-testid="sec-mfa-toggle" checked={!!sec.mfa_required} onCheckedChange={v => setSec({ ...sec, mfa_required: v })} />
        </div>
        <div className="flex items-center justify-between">
          <div><p className="text-sm font-medium">SCIM provisioning</p>
            <p className="text-xs" style={{ color: '#9a9e96' }}>Directory sync (integration in later phase)</p></div>
          <Switch data-testid="sec-scim-toggle" checked={!!sec.scim_enabled} onCheckedChange={v => setSec({ ...sec, scim_enabled: v })} />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-sm block mb-1" style={{ color: '#4b504a' }}>Session timeout (minutes)</label>
            <Input data-testid="sec-timeout-input" type="number" value={sec.session_timeout_minutes ?? 480}
              onChange={e => setSec({ ...sec, session_timeout_minutes: e.target.value })} className="bg-white border-[#D9D6CD]" />
          </div>
          <div>
            <label className="text-sm block mb-1" style={{ color: '#4b504a' }}>SSO provider</label>
            <select data-testid="sec-sso-select" value={sec.sso_provider || ''} onChange={e => setSec({ ...sec, sso_provider: e.target.value })}
              className="w-full h-9 px-3 rounded-md border bg-white text-sm" style={{ borderColor: '#D9D6CD' }}>
              <option value="">None</option>
              <option value="azure_ad">Microsoft Entra ID</option>
              <option value="google_workspace">Google Workspace</option>
              <option value="okta">Okta</option>
            </select>
          </div>
        </div>
        <div>
          <label className="text-sm block mb-1" style={{ color: '#4b504a' }}>IP allowlist (one CIDR per line)</label>
          <textarea data-testid="sec-ip-allowlist-input" value={ipText} onChange={e => setIpText(e.target.value)} rows={3}
            className="w-full rounded-md border px-3 py-2 text-sm" style={{ ...mono, borderColor: '#D9D6CD' }}
            placeholder="203.0.113.0/24" />
        </div>
      </div>
      <Button disabled={saving} data-testid="save-security-btn" onClick={save}
        className="mt-5 text-white hover:opacity-90" style={{ background: GREEN }}>Save security settings</Button>
    </Card>
  );
}

const LIFECYCLE_BADGE = {
  ACTIVE: { bg: '#E7F2EE', fg: GREEN }, PROVISIONED: { bg: '#EEF3FB', fg: '#2B4E8C' },
  DELIVERED: { bg: '#E7F2EE', fg: GREEN }, RETURNED: { bg: '#FBF3E6', fg: '#8C6A2B' },
  FAULTY: { bg: '#FBEBEB', fg: '#8C2B2B' },
};

function DevicesTab({ tenantId }) {
  const [devices, setDevices] = useState([]);
  const navigate = useNavigate();

  const load = useCallback(() => {
    adminGet(`/customers/${tenantId}/devices`).then(setDevices).catch(e => toast.error(e.message));
  }, [tenantId]);
  useEffect(() => { load(); }, [load]);

  const recall = async (d) => {
    const reason = window.prompt(`Recall ${d.trackotic_id}? Enter a reason:`);
    if (reason === null) return;
    try {
      await adminPost(`/devices/${d.id}/recall`, { reason: reason || 'Recalled by admin' });
      toast.success(`${d.trackotic_id} recalled — will re-enter QC queue`);
      load();
    } catch (e) { toast.error(e.message); }
  };

  return (
    <Card className="p-0 overflow-x-auto" data-testid="customer-devices-tab">
      <table className="w-full text-sm">
        <thead><tr className="text-left border-b" style={{ color: '#6b7069', borderColor: '#EDEBE4' }}>
          <th className="py-3 px-4 font-medium">Track0tic ID</th><th className="font-medium">Type</th>
          <th className="font-medium">Site</th><th className="font-medium">Lifecycle</th>
          <th className="font-medium">Connectivity</th><th className="font-medium pr-4"></th></tr></thead>
        <tbody>
          {devices.map(d => {
            const b = LIFECYCLE_BADGE[d.lifecycle_status] || { bg: '#F4F3EF', fg: '#6b7069' };
            return (
              <tr key={d.id} data-testid={`cust-device-row-${d.trackotic_id}`} className="border-b" style={{ borderColor: '#F3F1EA' }}>
                <td className="py-3 px-4">
                  <button className="underline decoration-dotted" style={{ ...mono, color: GREEN }}
                    data-testid={`cust-device-link-${d.trackotic_id}`}
                    onClick={() => navigate(`/admin/devices/${d.id}`)}>{d.trackotic_id}</button>
                </td>
                <td>{d.device_type || '—'}</td>
                <td>{d.site || '—'}</td>
                <td><span className="px-2 py-0.5 rounded text-xs" style={{ background: b.bg, color: b.fg }}>{d.lifecycle_status}</span></td>
                <td>{d.connectivity}</td>
                <td className="pr-4 text-right">
                  {['PROVISIONED', 'ACTIVE', 'DELIVERED'].includes(d.lifecycle_status) && (
                    <Button size="sm" variant="outline" data-testid={`recall-btn-${d.trackotic_id}`}
                      onClick={() => recall(d)} className="bg-white border-[#D9D6CD]" style={{ color: '#8C2B2B' }}>Recall</Button>
                  )}
                </td>
              </tr>
            );
          })}
          {devices.length === 0 && <tr><td colSpan={6} className="py-8 text-center" style={{ color: '#9a9e96' }}>No devices allocated yet</td></tr>}
        </tbody>
      </table>
    </Card>
  );
}

function AuditTab({ tenantId }) {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    adminGet(`/customers/${tenantId}/audit-log`).then(setRows).catch(e => toast.error(e.message));
  }, [tenantId]);

  return (
    <Card className="p-0 overflow-x-auto" data-testid="customer-audit-tab">
      <table className="w-full text-sm">
        <thead><tr className="text-left border-b" style={{ color: '#6b7069', borderColor: '#EDEBE4' }}>
          <th className="py-3 px-4 font-medium">When</th><th className="font-medium">Actor</th>
          <th className="font-medium">Action</th><th className="font-medium pr-4">Target</th></tr></thead>
        <tbody>
          {rows.map((a, i) => (
            <tr key={a.id || `${a.at}-${i}`} className="border-b" style={{ borderColor: '#F3F1EA' }}>
              <td className="py-2.5 px-4 whitespace-nowrap" style={mono}>{new Date(a.at).toLocaleString()}</td>
              <td>{a.actor || 'System'}</td>
              <td><span className="px-2 py-0.5 rounded text-xs" style={{ background: '#EFEDE6', color: '#4b504a', ...mono }}>{a.action}</span></td>
              <td className="pr-4">{a.target}</td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={4} className="py-8 text-center" style={{ color: '#9a9e96' }}>No audit entries yet</td></tr>}
        </tbody>
      </table>
    </Card>
  );
}

export default function AdminCustomerDetailPage() {
  const { tenantId } = useParams();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') || 'Overview';
  const [customer, setCustomer] = useState(null);

  const load = useCallback(() => {
    adminGet(`/customers/${tenantId}`).then(setCustomer).catch(e => toast.error(e.message));
  }, [tenantId]);
  useEffect(() => { load(); }, [load]);

  if (!customer) return <div className="p-8 text-sm" style={{ color: '#9a9e96' }}>Loading customer…</div>;

  return (
    <div className="p-8 max-w-6xl" data-testid="admin-customer-detail-page">
      <button data-testid="back-to-customers-btn" onClick={() => navigate('/admin/customers')}
        className="flex items-center gap-1.5 text-sm mb-4 hover:underline" style={{ color: '#6b7069' }}>
        <ArrowLeft size={15} /> Customers
      </button>
      <div className="flex items-center justify-between mb-1">
        <h1 className="text-4xl font-bold" style={{ color: '#1B1D1C' }}>{customer.legal_name}</h1>
        <Button variant="outline" data-testid="manage-users-btn" onClick={() => navigate(`/admin/users?customer=${customer.id}`)}
          className="bg-white border-[#D9D6CD]"><Users size={15} className="mr-1.5" />Users &amp; roles</Button>
      </div>
      <p className="text-sm mb-6" style={{ color: '#9a9e96' }}>
        <span style={mono}>{customer.code}</span> · {customer.vertical_code || 'No vertical'} · {customer.compliance_tier || '—'}
      </p>

      <div className="flex gap-1 mb-6 border-b flex-wrap" style={{ borderColor: '#E5E3DC' }}>
        {(customer.vertical_code === 'BFSI' ? [...TABS, ...BFSI_TABS] : TABS).map(t => (
          <button key={t} data-testid={`customer-tab-${t.toLowerCase().replace(/\s/g, '-')}`}
            onClick={() => setParams({ tab: t })}
            className="px-4 py-2.5 text-sm font-medium -mb-px border-b-2 transition-colors"
            style={tab === t ? { borderColor: GREEN, color: GREEN } : { borderColor: 'transparent', color: '#6b7069' }}>
            {t}
          </button>
        ))}
      </div>

      {tab === 'Overview' && <OverviewTab customer={customer} onSaved={load} />}
      {tab === 'Features' && <FeaturesTab tenantId={tenantId} />}
      {tab === 'Security' && <><SecurityTab tenantId={tenantId} /><TenantIpPolicyCard tenantId={tenantId} /></>}
      {tab === 'Devices' && <DevicesTab tenantId={tenantId} />}
      {tab === 'Audit trail' && <AuditTab tenantId={tenantId} />}
      {tab === 'BFSI setup' && <BfsiSetupTab tenantId={tenantId} />}
      {tab === 'Rules & branches' && <BfsiRulesTab tenantId={tenantId} />}
      {tab === 'Provisioning' && <BfsiProvisioningTab tenantId={tenantId} />}
      {tab === 'Compliance' && <BfsiComplianceTab tenantId={tenantId} />}
      {tab === 'Ops health' && <BfsiOpsHealthTab tenantId={tenantId} />}
    </div>
  );
}
